export type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';

interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  bottom_bar_bg_color?: string;
}

interface TelegramWebApp {
  initData?: string;
  ready?: () => void;
  expand?: () => void;
  disableVerticalSwipes?: () => void;
  colorScheme?: 'light' | 'dark';
  themeParams?: TelegramThemeParams;
  initDataUnsafe?: { start_param?: string; user?: { language_code?: string } };
  HapticFeedback?: { impactOccurred?: (style: HapticStyle) => void; notificationOccurred?: (type: 'error' | 'success' | 'warning') => void };
  CloudStorage?: { getItem?: (key: string, callback: (error: Error | null, value?: string) => void) => void; setItem?: (key: string, value: string, callback?: (error: Error | null, success?: boolean) => void) => void };
  openTelegramLink?: (url: string) => void;
  openLink?: (url: string) => void;
  onEvent?: (event: 'themeChanged', callback: () => void) => void;
  offEvent?: (event: 'themeChanged', callback: () => void) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

function webApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

function isInsideTelegram(): boolean {
  return Boolean(webApp()?.initData);
}

function applyTheme(): 'light' | 'dark' {
  const app = isInsideTelegram() ? webApp() : undefined;
  const root = document.documentElement;
  const scheme = app?.colorScheme ?? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  root.dataset.theme = scheme;
  const mapping: Array<[keyof TelegramThemeParams, string]> = [
    ['bg_color', '--tg-theme-bg-color'], ['text_color', '--tg-theme-text-color'], ['hint_color', '--tg-theme-hint-color'],
    ['link_color', '--tg-theme-link-color'], ['button_color', '--tg-theme-button-color'], ['button_text_color', '--tg-theme-button-text-color'],
    ['secondary_bg_color', '--tg-theme-secondary-bg-color'], ['header_bg_color', '--tg-theme-header-bg-color'], ['bottom_bar_bg_color', '--tg-theme-bottom-bar-bg-color'],
  ];
  for (const [key, variable] of mapping) {
    const value = app?.themeParams?.[key];
    if (value) root.style.setProperty(variable, value);
  }
  return scheme;
}

export const telegram = {
  initialize(): () => void {
    const app = isInsideTelegram() ? webApp() : undefined;
    app?.ready?.();
    app?.expand?.();
    app?.disableVerticalSwipes?.();
    applyTheme();
    const listener = () => applyTheme();
    app?.onEvent?.('themeChanged', listener);
    return () => app?.offEvent?.('themeChanged', listener);
  },
  haptic(style: HapticStyle = 'light'): void {
    if (isInsideTelegram()) webApp()?.HapticFeedback?.impactOccurred?.(style);
  },
  notify(type: 'error' | 'success' | 'warning'): void {
    if (isInsideTelegram()) webApp()?.HapticFeedback?.notificationOccurred?.(type);
  },
  openTelegramLink(url: string): void {
    if (isInsideTelegram() && webApp()?.openTelegramLink) webApp()?.openTelegramLink?.(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  },
  openExternalLink(url: string): void {
    if (isInsideTelegram() && webApp()?.openLink) webApp()?.openLink?.(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  },
  getStartParam(): string | null {
    const unsafe = isInsideTelegram() ? webApp()?.initDataUnsafe?.start_param : undefined;
    if (unsafe) return unsafe;
    const parameters = new URLSearchParams(window.location.search);
    return parameters.get('tgWebAppStartParam') ?? parameters.get('start_param') ?? (parameters.get('share') ? `s_${parameters.get('share')}` : null);
  },
  getLanguage(): 'ru' | 'en' | null {
    const code = isInsideTelegram() ? webApp()?.initDataUnsafe?.user?.language_code : undefined;
    if (!code) return null;
    return code.toLocaleLowerCase().startsWith('ru') ? 'ru' : 'en';
  },
  async storageGet(key: string): Promise<string | null> {
    const storage = isInsideTelegram() ? webApp()?.CloudStorage : undefined;
    if (storage?.getItem) {
      const cloudValue = await new Promise<string | null>((resolve) => storage.getItem?.(key, (error, value) => resolve(error ? null : value ?? null)));
      if (cloudValue !== null) return cloudValue;
    }
    return localStorage.getItem(key);
  },
  async storageSet(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
    const storage = isInsideTelegram() ? webApp()?.CloudStorage : undefined;
    if (storage?.setItem) await new Promise<void>((resolve) => storage.setItem?.(key, value, () => resolve()));
  },
};
